import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space, Card, Tag, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { SEARCH_TAGS } from '../../mutations/tags';

const { Option } = Select;

// Lista de estados brasileiros
const brazilianStates = [
  { id: 'AC', name: 'Acre', initials: 'AC' },
  { id: 'AL', name: 'Alagoas', initials: 'AL' },
  { id: 'AP', name: 'Amapá', initials: 'AP' },
  { id: 'AM', name: 'Amazonas', initials: 'AM' },
  { id: 'BA', name: 'Bahia', initials: 'BA' },
  { id: 'CE', name: 'Ceará', initials: 'CE' },
  { id: 'DF', name: 'Distrito Federal', initials: 'DF' },
  { id: 'ES', name: 'Espírito Santo', initials: 'ES' },
  { id: 'GO', name: 'Goiás', initials: 'GO' },
  { id: 'MA', name: 'Maranhão', initials: 'MA' },
  { id: 'MT', name: 'Mato Grosso', initials: 'MT' },
  { id: 'MS', name: 'Mato Grosso do Sul', initials: 'MS' },
  { id: 'MG', name: 'Minas Gerais', initials: 'MG' },
  { id: 'PA', name: 'Pará', initials: 'PA' },
  { id: 'PB', name: 'Paraíba', initials: 'PB' },
  { id: 'PR', name: 'Paraná', initials: 'PR' },
  { id: 'PE', name: 'Pernambuco', initials: 'PE' },
  { id: 'PI', name: 'Piauí', initials: 'PI' },
  { id: 'RJ', name: 'Rio de Janeiro', initials: 'RJ' },
  { id: 'RN', name: 'Rio Grande do Norte', initials: 'RN' },
  { id: 'RS', name: 'Rio Grande do Sul', initials: 'RS' },
  { id: 'RO', name: 'Rondônia', initials: 'RO' },
  { id: 'RR', name: 'Roraima', initials: 'RR' },
  { id: 'SC', name: 'Santa Catarina', initials: 'SC' },
  { id: 'SP', name: 'São Paulo', initials: 'SP' },
  { id: 'SE', name: 'Sergipe', initials: 'SE' },
  { id: 'TO', name: 'Tocantins', initials: 'TO' }
];

// Opções de campos disponíveis para filtro
const fieldOptions = [
  { label: 'Tags', value: 'tags', type: 'array' },
  { label: 'Nome', value: 'name', type: 'text' },
  { label: 'Apelido', value: 'nickname', type: 'text' },
  { label: 'Documento', value: 'document', type: 'text' },
  { label: 'WhatsApp', value: 'whatsapp', type: 'whatsapp_validation' },
  { label: 'Telefone', value: 'phone', type: 'text' },
  { label: 'Observação', value: 'observation', type: 'text' },
  { label: 'Contato Principal', value: 'mainContact', type: 'text' },
  { label: 'Código', value: 'reference', type: 'text' },
  { label: 'Inscrição Estadual', value: 'stateRegistration', type: 'text' },
  { label: 'Cidade', value: 'city', type: 'text' },
  { label: 'Estado', value: 'state', type: 'array' },
  { label: 'Status', value: 'active', type: 'boolean' },
];

// Operadores por tipo de campo
const operatorsByType = {
  text: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está em branco', value: 'is_empty' },
    { label: 'Está preenchido', value: 'is_not_empty' },
  ],
  array: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está vazio', value: 'is_empty' },
    { label: 'Não está vazio', value: 'is_not_empty' },
  ],
  boolean: [
    { label: 'É ativo', value: 'is_active' },
    { label: 'É inativo', value: 'is_inactive' },
    { label: 'Ambos', value: 'both' },
  ],
  whatsapp_validation: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está em branco', value: 'is_empty' },
    { label: 'Está preenchido', value: 'is_not_empty' },
    { label: 'Validado', value: 'whatsapp_verified' },
    { label: 'Não validado', value: 'whatsapp_not_verified' },
  ]
};

const AdvancedFilterModal = ({ open, onCancel, onApply, initialFilters = [] }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState([]);
  const [formValues, setFormValues] = useState({});

  // Buscar tags para o filtro de tags
  const { data: tagsData } = useQuery(SEARCH_TAGS, {
    variables: { query: '*' }, // Buscar todas as tags
    skip: !open,
    fetchPolicy: 'network-only'
  });



  const tags = tagsData?.searchTags || [];



  useEffect(() => {
    if (open) {
      if (initialFilters.length > 0) {
        // Converter filtros iniciais
        const convertedFilters = initialFilters.map((filter, index) => ({
          id: filter.id || Date.now() + index,
          field: filter.field,
          operator: filter.operator,
          fieldType: filter.fieldType
        }));
        
        setFilters(convertedFilters);
        
        // Preencher formulário
        const initialValues = {};
        initialFilters.forEach((filter, index) => {
          initialValues[`field_${index}`] = filter.field;
          initialValues[`operator_${index}`] = filter.operator;
          initialValues[`value_${index}`] = filter.value;
        });
        
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
      } else {
        addFilter();
      }
    } else {
      setFilters([]);
      form.resetFields();
      setFormValues({});
    }
  }, [open, initialFilters]);

  const addFilter = () => {
    setFilters(prevFilters => {
      const newFilter = {
        id: Date.now(),
        field: 'tags',
        operator: 'contains',
        fieldType: 'array'
      };
      const newFilters = [...prevFilters, newFilter];

      // Inicializar valores padrão no form
      const newIndex = prevFilters.length;
      const defaultValues = {
        [`field_${newIndex}`]: 'tags',
        [`operator_${newIndex}`]: 'contains'
      };
      form.setFieldsValue(defaultValues);
      setFormValues(prev => ({ ...prev, ...defaultValues }));

      return newFilters;
    });
  };

  const removeFilter = (filterId) => {
    const filterIndex = filters.findIndex(f => f.id === filterId);
    const newFilters = filters.filter(f => f.id !== filterId);
    setFilters(newFilters);

    // Reorganizar campos do form após remoção
    const currentValues = form.getFieldsValue();
    const newValues = {};

    newFilters.forEach((filter, newIndex) => {
      const oldIndex = filters.findIndex(f => f.id === filter.id);
      if (oldIndex !== -1 && oldIndex !== newIndex) {
        // Mover valores para o novo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            const newKey = key.replace(`_${oldIndex}`, `_${newIndex}`);
            newValues[newKey] = currentValues[key];
          }
        });
      } else if (oldIndex === newIndex) {
        // Manter valores no mesmo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            newValues[key] = currentValues[key];
          }
        });
      }
    });

    form.setFieldsValue(newValues);
    setFormValues(newValues);
  };

  const handleApply = () => {
    form.validateFields().then(values => {
      const processedFilters = filters.map((filter, index) => {
        const field = values[`field_${index}`];
        const operator = values[`operator_${index}`];
        const value = values[`value_${index}`];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);

        return {
          id: filter.id,
          field,
          operator,
          value,
          fieldType: fieldConfig?.type || 'text'
        };
      }).filter(filter => {
        // Remover filtros incompletos
        if (['is_empty', 'is_not_empty', 'is_active', 'is_inactive', 'both', 'whatsapp_verified', 'whatsapp_not_verified'].includes(filter.operator)) {
          return true; // Estes operadores não precisam de valor
        }
        return filter.value !== undefined && filter.value !== null && filter.value !== '';
      });

      onApply(processedFilters);
    }).catch(errorInfo => {
      console.error('Validation Failed:', errorInfo);
    });
  };

  const renderValueInput = (filter, index) => {
    const field = formValues[`field_${index}`] || 'tags';
    const operator = formValues[`operator_${index}`] || 'contains';
    const fieldConfig = fieldOptions.find(opt => opt.value === field);

    // Operadores que não precisam de valor
    if (['is_empty', 'is_not_empty', 'is_active', 'is_inactive', 'both', 'whatsapp_verified', 'whatsapp_not_verified'].includes(operator)) {
      return null;
    }

    if (field === 'tags') {
      return (
        <Form.Item
          name={`value_${index}`}
          label="Tags"
          rules={[{ required: true, message: 'Selecione as tags' }]}
        >
          <Select
            mode="multiple"
            placeholder="Selecione as tags"
            allowClear
            showSearch
            filterOption={(input, option) => {
              // Buscar o nome da tag pelo value (id)
              const tag = tags.find(t => t.id === option.value);
              return tag?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {tags.map(tag => (
              <Option key={tag.id} value={tag.id}>
                <Space>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: tag.backgroundColor || '#6fa8dc',
                      borderRadius: 2,
                      display: 'inline-block'
                    }}
                  />
                  {tag.name}
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (field === 'city') {
      return (
        <Form.Item
          name={`value_${index}`}
          label="Cidade"
          rules={[{ required: true, message: 'Digite o nome da cidade' }]}
        >
          <Input placeholder="Digite o nome da cidade" />
        </Form.Item>
      );
    }

    if (field === 'state') {
      return (
        <Form.Item
          name={`value_${index}`}
          label="Estados"
          rules={[{ required: true, message: 'Selecione os estados' }]}
        >
          <Select
            mode="multiple"
            placeholder="Selecione os estados"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const state = brazilianStates.find(s => s.id === option.value);
              return state?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                     state?.initials.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {brazilianStates.map(state => (
              <Option key={state.id} value={state.id}>
                {state.name} ({state.initials})
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (field === 'active') {
      // Para campos boolean, os operadores não precisam de valor
      return null;
    }

    return (
      <Form.Item
        name={`value_${index}`}
        label="Valor"
        rules={[{ required: true, message: 'Digite o valor' }]}
      >
        <Input placeholder="Digite o valor para filtrar" />
      </Form.Item>
    );
  };

  const onFormValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
    
    // Se mudou o campo, resetar o operador para o padrão do tipo
    Object.keys(changedValues).forEach(key => {
      if (key.startsWith('field_')) {
        const index = key.split('_')[1];
        const field = changedValues[key];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);
        const defaultOperator = operatorsByType[fieldConfig?.type || 'text'][0].value;
        
        form.setFieldsValue({
          [`operator_${index}`]: defaultOperator,
          [`value_${index}`]: undefined // Limpar valor quando mudar o campo
        });
        
        // Atualizar o filtro na lista
        setFilters(prevFilters => {
          const newFilters = [...prevFilters];
          if (newFilters[index]) {
            newFilters[index].field = field;
            newFilters[index].operator = defaultOperator;
            newFilters[index].fieldType = fieldConfig?.type || 'text';
          }
          return newFilters;
        });
      }
    });
  };

  const getFilterDescription = (filter, index) => {
    const field = formValues[`field_${index}`] || filter.field || 'tags';
    const operator = formValues[`operator_${index}`] || filter.operator || 'contains';
    
    const fieldLabel = fieldOptions.find(opt => opt.value === field)?.label || field;
    const fieldConfig = fieldOptions.find(opt => opt.value === field);
    const operatorLabel = operatorsByType[fieldConfig?.type || 'text'].find(opt => opt.value === operator)?.label || operator;

    return `${fieldLabel} - ${operatorLabel}`;
  };

  return (
    <Modal
      title="Filtros Avançados"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="clear" onClick={() => {
          setFilters([]);
          form.resetFields();
          onApply([]);
        }}>
          Limpar Filtros
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Aplicar Filtros
        </Button>
      ]}
      width={900}
      maskClosable={false}
      translate="no"
      className="notranslate"
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onFormValuesChange}
      >
        {filters.map((filter, index) => (
          <Card
            key={filter.id}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <span>Filtro {index + 1}</span>
                <Tag color="blue">{getFilterDescription(filter, index)}</Tag>
              </Space>
            }
            extra={
              filters.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilter(filter.id)}
                >
                  Remover
                </Button>
              )
            }
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={`field_${index}`}
                  label="Campo"
                  initialValue="tags"
                  rules={[{ required: true, message: 'Selecione o campo' }]}
                >
                  <Select placeholder="Selecione o campo">
                    {fieldOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={`operator_${index}`}
                  label="Operador"
                  initialValue="contains"
                  rules={[{ required: true, message: 'Selecione o operador' }]}
                >
                  <Select placeholder="Selecione o operador">
                    {(() => {
                      const field = formValues[`field_${index}`] || 'tags';
                      const fieldConfig = fieldOptions.find(opt => opt.value === field);
                      const operators = operatorsByType[fieldConfig?.type || 'text'] || operatorsByType.text;
                      return operators.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                {renderValueInput(filter, index)}
              </Col>
            </Row>
          </Card>
        ))}

        <Button
          type="dashed"
          onClick={addFilter}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 16 }}
        >
          Adicionar Filtro
        </Button>
      </Form>
    </Modal>
  );
};

export default AdvancedFilterModal;